import { Anchor, List, Text } from '@mantine/core'

import { Section } from '../../components/SectionHeader'
import { LegalPage } from './LegalPage'

/** `/terms`: no sign-in needed. The source text is also kept in docs/legal/terms.md; keep the two in sync. */
export function TermsPage() {
  return (
    <LegalPage title="Terms of service">
      <Text size="sm">
        These terms cover your use of Macrotide, a personal nutrition and training app from Ikaros Dev
        (macrotide@ikarosdev.com). By using the app you agree to them.
      </Text>

      <Section title="Not medical advice" card={false}>
        <Text size="sm">
          Macrotide and its AI features estimate nutrition and suggest training. They are not medical advice. Talk
          to a doctor, dietitian or qualified professional before making health decisions, especially around weight
          loss, injuries or medical conditions.
        </Text>
      </Section>

      <Section title="AI estimates can be wrong" card={false}>
        <Text size="sm">
          Nutrition read from a photo or description, and training plans adjusted by AI, are estimates. Check them
          before you rely on them. We work to make them accurate, but we can&apos;t guarantee it.
        </Text>
        <Text size="sm" mt="xs">
          AI features are generated by AI models from other companies (see our{' '}
          <Anchor href="/privacy">privacy policy</Anchor>), and their results are marked as AI results in the app. If
          a result is wrong or offensive, use &quot;Report this result&quot; so we can look at it.
        </Text>
      </Section>

      <Section title="You're responsible for your inputs" card={false}>
        <Text size="sm">
          You&apos;re responsible for what you log, and what you confirm into your food log, recipes, programs and
          photos.
        </Text>
      </Section>

      <Section title="Age" card={false}>
        <Text size="sm">
          Macrotide is for people 18 and over. Setup asks for your birth date, which also improves your calorie
          estimates, and stops if you&apos;re under 18.
        </Text>
      </Section>

      <Section title="Acceptable use" card={false}>
        <Text size="sm">Don&apos;t use Macrotide to break the law, interfere with the service, or access data that isn&apos;t yours.</Text>
      </Section>

      <Section title="Prohibited content" card={false}>
        <Text size="sm">Don&apos;t send Macrotide, including its AI features, any of these:</Text>
        <List size="sm">
          <List.Item>
            sexual content involving anyone under 18 (child sexual abuse material), in any form, including drawn or
            AI-generated;
          </List.Item>
          <List.Item>sexual or pornographic content;</List.Item>
          <List.Item>graphic violence or gore;</List.Item>
          <List.Item>content that is hateful, harasses or threatens anyone;</List.Item>
          <List.Item>content that promotes or supports terrorism or violent extremism;</List.Item>
          <List.Item>
            anything else that is illegal to share or possess, or that infringes someone else&apos;s rights;
          </List.Item>
          <List.Item>attempts to make the AI ignore its instructions or produce any of the above.</List.Item>
        </List>
        <Text size="sm" mt="xs">
          Content about self-harm or suicide isn&apos;t against these terms. If something you send suggests you might
          be struggling, the app shows where to find support (for example Samaritans, 116 123 in the UK) and nothing
          else happens.
        </Text>
      </Section>

      <Section title="How we moderate" card={false}>
        <Text size="sm">
          Each AI request is also checked by the AI for prohibited content. When it flags a request, your AI features
          are paused at once while a person reviews it, and the request is kept as evidence (see our{' '}
          <Anchor href="/privacy">privacy policy</Anchor>, &quot;Content checks and holds&quot;); everything else
          keeps working. A person then either lifts the hold or decides on a restriction. Suspected child sexual
          abuse material suspends the account straight away. AI rate-limit strikes (see &quot;Fair use&quot;) are
          applied automatically. Bans, and reports to the authorities, are always decided by a person.
        </Text>
        <Text size="sm" mt="xs">
          We report child sexual abuse material to the Internet Watch Foundation and the National Crime Agency,
          information about terrorism to Counter Terrorism Policing, and threats to someone&apos;s life or safety to
          the police.
        </Text>
      </Section>

      <Section title="Fair use" card={false}>
        <Text size="sm">
          AI features have per-minute limits so they stay available for everyone. Repeated overuse pauses your AI
          access: first for 1 hour, then for 24 hours. A third time suspends your account: you can still download
          your data, and it&apos;s deleted after 30 days. The email address on a suspended account can&apos;t be
          used to sign up again for 12 months. We don&apos;t accept disposable or temporary email addresses.
        </Text>
        <Text size="sm" mt="xs">
          You can appeal by emailing macrotide@ikarosdev.com — a person reviews every suspension.
        </Text>
      </Section>

      <Section title="Restrictions, reasons and appeals" card={false}>
        <Text size="sm">If you break these terms, or to protect the service or other people, we may:</Text>
        <List size="sm">
          <List.Item>
            <b>limit</b> your account: lower AI limits, or turn single features off, for a set time;
          </List.Item>
          <List.Item>
            <b>pause</b> your AI features for a set time, or while a person reviews a flagged request (a{' '}
            <b>hold</b>);
          </List.Item>
          <List.Item>
            <b>suspend</b> your account: only exporting your data and deleting the account still work, and the
            account is deleted after 30 days unless we decide to keep it;
          </List.Item>
          <List.Item>
            <b>ban</b> your account: a suspension, plus the email address can&apos;t be used to sign up again for 12
            months.
          </List.Item>
        </List>
        <Text size="sm" mt="xs">
          When we restrict your account, we tell you in the app or by email: what we restricted and for how long, the
          facts behind it, whether it was decided automatically, which part of these terms (or which law) it&apos;s
          based on, and how to appeal. We may leave details out where the law requires, for example if telling you
          could harm a police investigation.
        </Text>
        <Text size="sm" mt="xs">
          To appeal, email macrotide@ikarosdev.com, saying why you think the decision is wrong. A person reviews every
          appeal and tells you the outcome and the reasons. If you&apos;re in the EU, you can also complain to the
          Digital Services Coordinator in your country, or go to court.
        </Text>
      </Section>

      <Section title="Reporting illegal content" card={false}>
        <Text size="sm">
          If you think something on Macrotide is illegal, email macrotide@ikarosdev.com with: where it is, why you
          think it&apos;s illegal, your name and email address (you can leave these out when reporting child sexual
          abuse material), and a statement that you believe your report is accurate and complete. We&apos;ll confirm
          we&apos;ve received it, decide without undue delay, and tell you what we did. To report child sexual abuse
          images directly, use the Internet Watch Foundation (iwf.org.uk).
        </Text>
      </Section>

      <Section title="Your content, and the licence you give us" card={false}>
        <Text size="sm">
          Food logs, recipes, photos and everything else you create stays yours. We only process it to provide the
          service: that includes sending what a feature needs to our AI providers when you use an AI feature (see
          our <Anchor href="/privacy">privacy policy</Anchor>), and, when you choose to send a product to Open Food
          Facts, publishing it there.
        </Text>
        <Text size="sm" mt="xs">
          When you send a product to Open Food Facts, you agree that it&apos;s published there openly, the data
          under the Open Database Licence (ODbL) and photos under CC BY-SA, and that it stays published even if you
          delete your Macrotide account.
        </Text>
      </Section>

      <Section title="Open data licences" card={false}>
        <Text size="sm">Macrotide&apos;s food and exercise data comes from several open sources, each under its own licence:</Text>
        <List size="sm">
          <List.Item>
            <b>Open Food Facts</b> — barcode data, Open Database Licence (ODbL).
          </List.Item>
          <List.Item>
            <b>USDA FoodData Central</b> — Foundation Foods, SR Legacy and FNDDS, public domain (CC0 1.0).
          </List.Item>
          <List.Item>
            <b>CoFID</b> (UK) — Open Government Licence v3.0.
          </List.Item>
          <List.Item>
            <b>CIQUAL</b> (France) — Licence Ouverte / Open Licence v2.0 (Etalab).
          </List.Item>
          <List.Item>
            <b>Bundeslebensmittelschlüssel (BLS)</b> (Germany) — CC BY 4.0.
          </List.Item>
          <List.Item>
            <b>MEXT</b> (Japan) — Standard Tables of Food Composition in Japan.
          </List.Item>
          <List.Item>
            <b>free-exercise-db</b> — public domain, Unlicense.
          </List.Item>
          <List.Item>
            <b>wger</b> — each exercise under its own licence (CC BY-SA 4.0, CC BY-SA 3.0 or CC0 1.0), shown on its
            own screen in the app.
          </List.Item>
        </List>
        <Text size="sm" mt="xs">
          Type, icon and body-diagram credits are on the About page in the app.
        </Text>
      </Section>

      <Section title="Deleting your account" card={false}>
        <Text size="sm">
          You can delete your account at any time — see <Anchor href="/delete-account">/delete-account</Anchor> for
          how, and what gets deleted.
        </Text>
      </Section>

      <Section title="Liability" card={false}>
        <Text size="sm">
          Nothing in these terms limits our liability for death or personal injury caused by our negligence, for
          fraud, or for anything else the law doesn&apos;t let us limit, and nothing affects your legal rights as a
          consumer. Macrotide is free and provided as it is; we don&apos;t promise it will be error-free or always
          available. Otherwise we&apos;re only responsible for foreseeable loss caused by our breaking these terms or
          not using reasonable care and skill (including damage to your device or other content caused by the app),
          and not for business losses.
        </Text>
      </Section>

      <Section title="Governing law" card={false}>
        <Text size="sm">
          These terms are governed by the law of England and Wales. If you live in Scotland or Northern Ireland, you
          can also bring a claim in the courts there, and nothing here removes the consumer protections your local
          law gives you.
        </Text>
      </Section>

      <Section title="Changes" card={false}>
        <Text size="sm">
          If we make a material change to these terms, we&apos;ll update the date above and email account holders
          before the change takes effect.
        </Text>
      </Section>

      <Section title="Contact" card={false}>
        <Text size="sm">
          macrotide@ikarosdev.com, in English. This is also our point of contact for users and authorities under the
          EU Digital Services Act. <b>EU legal representative (Digital Services Act):</b> [To be decided by the
          owner].
        </Text>
      </Section>
    </LegalPage>
  )
}
